import {
  ClientEmail as ClientEmailRaw,
  ClientPhoneNumber as ClientPhoneNumberRaw,
} from '@provision/entity-types';
import { useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { MERGE_CLIENT_MUTATION } from 'src/graphql/entities/client/client.operations';
import { Client, ClientEmail, ClientPhone } from 'src/graphql/entities/client/client.types';
import { useNotifications } from 'src/utils/hooks';

export type MergeClient = Pick<Client, 'clientId' | 'firstName' | 'lastName' | 'partnerId'> & {
  fullName?: string;
  emails?: (Partial<ClientEmail> | ClientEmailRaw)[];
  phoneNumbers?: (Partial<ClientPhone> | ClientPhoneNumberRaw)[];
};

export interface MergeClientValues {
  clientOrigin: MergeClient;
  clientTarget: MergeClient;
}

export const useMergeClient = () => {
  const { showErrorAlert } = useNotifications();

  const [mergeClientMutation, { loading }] = useMutation<{
    mergeClient: {
      error: string | null;
      success: boolean;
    };
  }>(MERGE_CLIENT_MUTATION, {
    onError: () => showErrorAlert('Something went wrong merging clients, please try again'),
  });

  const mergeClient = useCallback(
    async (values: MergeClientValues) => {
      const { clientOrigin, clientTarget } = values;

      const { data } = await mergeClientMutation({
        variables: {
          clientOriginId: clientOrigin.clientId,
          clientTargetId: clientTarget.clientId,
        },
        optimisticResponse: {
          mergeClient: {
            error: null,
            success: true,
          },
        },
        refetchQueries: [
          'GetClient',
        ],
        update: (cache, { data: response }) => {
          const updated = response?.mergeClient;
          if (!updated?.success) return;

          const originId = cache.identify({
            __typename: 'Client',
            id: clientOrigin.clientId,
          });

          cache.evict({
            id: originId,
          });

          cache.gc();
        },
      });

      return data?.mergeClient;
    },
    [mergeClientMutation],
  );

  return { mergeClient, isLoading: loading };
};

